'use client';

import { useCallback } from 'react';
import { useAnalytics } from '../Analytics';
import { useAnalyticsPageView } from '../Analytics/AnalyticsPageView';
import type { NwStandardTrackInputMap } from '../generated';

/** Emitted when neither a page name nor a pathname is resolvable (non-browser render). */
const UNKNOWN_PAGE_NAME = 'unknown';

/** NW schemas that require top-level event_id (view / form lifecycle). */
const EVENTS_REQUIRING_EVENT_ID = new Set<keyof NwStandardTrackInputMap>([
  'Element Viewed',
  'Link Viewed',
  'Form Action',
]);

/**
 * Stamps NW standard common properties on every track call.
 * Auto-generates event_id only for view/lifecycle events; interact events
 * pair via parent_event_id. Callers may still pass event_id explicitly.
 */
export function useStandardTrack() {
  const { analytics, deployableName, deployableVersion, gitSha } = useAnalytics();
  const { pageViewId, pageName } = useAnalyticsPageView();

  return useCallback(
    <E extends keyof NwStandardTrackInputMap>(eventName: E, props: NwStandardTrackInputMap[E]) => {
      // Opinionate requires page_url + page_name — fall back rather than drop the event.
      const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
      const path = pageUrl ? new URL(pageUrl).pathname : '';
      const resolvedPageName = pageName || path || UNKNOWN_PAGE_NAME;

      return analytics?.track(
        eventName as string,
        {
          pageview_id: pageViewId,
          page_name: resolvedPageName,
          page_url: pageUrl,
          deployable_name: deployableName,
          deployable_version: deployableVersion,
          ...(EVENTS_REQUIRING_EVENT_ID.has(eventName) && !('event_id' in props && props.event_id)
            ? { event_id: crypto.randomUUID() }
            : {}),
          ...props,
        },
        {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        },
      );
    },
    [analytics, deployableName, deployableVersion, gitSha, pageViewId, pageName],
  );
}
